/**
 * Smart Paste v3 — Pipeline de 7 pasos para Cifraclub/UG/ChordPro
 *
 * 1. Limpieza de basura estructural
 * 2. Extraccion de metadatos (titulo, artista, tono, capo)
 * 3. Eliminacion de tablatura
 * 4. Deteccion de lineas de acordes
 * 5. Conversion de secciones
 * 6. Merge de acordes + letra
 * 7. Pipeline principal
 */

export type DetectedFormat = "chordpro" | "cifraclub" | "ug" | "unknown";

export interface SmartPasteResult {
  format: DetectedFormat;
  original: string;
  processed: string;
  removedLines: string[];
  hasChords: boolean;
  isValid: boolean;
  metadata?: {
    title: string;
    artist: string;
    key: string;
    capo: number;
  };
}

// ============================================
// PASO 0: Regex maestro de token de acorde
// ============================================
const CHORD_TOKEN = /[A-G][#b]?(?:min|maj|sus|dim|aug|add|m|M|°)?(?:\d+)?(?:[+#bM])?(?:\/[A-G][#b]?)?/g;

const CHORD_TOKEN_TEST = /^[A-G][#b]?(?:min|maj|sus|dim|aug|add|m|M|°)?(?:\d+)?(?:[+#bM])?(?:\/[A-G][#b]?)?$/;

// ============================================
// PASO 1: Limpieza de basura estructural
// ============================================
const JUNK_PATTERNS = [
  /^CIFRACLUB.*/i,
  /^Acordes para.*/i,
  /^Acordes:.*/i,
  /^Agregar a Favoritos.*/i,
  /^Enviado por.*/i,
  /^Publicidad.*/i,
  /^Curso de.*/i,
  /^www\..*/i,
  /^©.*/i,
  /^Tabbed by.*/i,
  /^Ultimate Guitar.*/i,
  /^MediaFire.*/i,
  /^.*\.(part\d+|rar|zip|7z).*$/i,
  /^\s*$/,  /^Tono:/i,
  /^Capo/i,
  /^\(?\s*\d+\s*\.?\s*º\s*traste\)?/i,];

function cleanLines(lines: string[]): string[] {
  return lines
    .map((l) => l.trimEnd())
    .filter((l) => !JUNK_PATTERNS.some((p) => p.test(l)));
}

// ============================================
// PASO 2: Extraccion de metadatos
// ============================================
function extractMetadata(lines: string[]) {
  const meta = { title: "", artist: "", key: "", capo: 0 };

  // Solo buscar titulo en la primera linea no vacia (indice 0 o 1)
  for (let i = 0; i < Math.min(2, lines.length); i++) {
    const line = lines[i].trim();
    if (
      !meta.title &&
      line.length > 0 &&
      line.length <= 40 &&
      !line.startsWith("[") &&
      !isChordLine(line) &&
      !/^Capo/i.test(line) &&
      !/^Tono:/i.test(line) &&
      !/^Intro/i.test(line) &&
      !/^\s*\(/.test(line)
    ) {
      meta.title = line;
      if (
        lines[i + 1] &&
        !lines[i + 1].startsWith("[") &&
        !isChordLine(lines[i + 1]) &&
        !/^Capo/i.test(lines[i + 1]) &&
        !/^Tono:/i.test(lines[i + 1])
      ) {
        meta.artist = lines[i + 1].trim();
      }
      break;
    }
  }

  const keyLine = lines.find((l) => /^Tono:\s*/i.test(l));
  if (keyLine) {
    const match = keyLine.match(/^Tono:\s*([A-G][#b]?)/i);
    if (match) meta.key = match[1];
  }

  const capoLine = lines.find((l) => /Capo/i.test(l));
  if (capoLine) {
    const match = capoLine.match(/(\d+)/);
    if (match) meta.capo = parseInt(match[1], 10);
  }

  return meta;
}

// ============================================
// PASO 3: Deteccion y eliminacion de tablatura
// ============================================
function isTablatureLine(line: string): boolean {
  // Detectar E|, B|, G|, D|, A|, e| en CUALQUIER posicion
  return /[EBGDAe]\|/.test(line);
}

function removeTablature(lines: string[]): string[] {
  return lines.filter((l) => !isTablatureLine(l));
}

// ============================================
// PASO 4: Deteccion de secciones y acordes
// ============================================
function isPureSection(line: string): boolean {
  return /^\s*\[([^\]]+)\]\s*$/.test(line);
}

export function isChordLine(line: string): boolean {
  const trimmed = line.trim();
  if (trimmed === "") return false;

  // Una seccion pura NO es linea de acordes
  if (isPureSection(trimmed)) return false;

  const tokens = trimmed.split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return false;

  const chordMatches = tokens.filter((t) => CHORD_TOKEN_TEST.test(t));
  return chordMatches.length / tokens.length >= 0.7;
}

// ============================================
// PASO 5: Conversion de secciones
// ============================================
function convertSectionLine(line: string): string {
  const match = line.match(/^\[(.*?)\]$/);
  if (!match) return line;
  const content = match[1].trim();
  return `{comment: ${content}}`;
}

// ============================================
// PASO 6: Merge de acordes + letra
// ============================================
function mergeChordLineWithLyric(chordLine: string, lyricLine: string): string {
  const chords: { chord: string; start: number }[] = [];
  let match: RegExpExecArray | null;
  const chordRegex = new RegExp(CHORD_TOKEN.source, "g");
  while ((match = chordRegex.exec(chordLine)) !== null) {
    chords.push({
      chord: match[0],
      start: match.index,
    });
  }

  if (chords.length === 0) return lyricLine;

  const words: { text: string; start: number }[] = [];
  const wordRegex = /\S+/g;
  while ((match = wordRegex.exec(lyricLine)) !== null) {
    words.push({ text: match[0], start: match.index });
  }

  if (words.length === 0) {
    return chords.map((c) => `[${c.chord}]`).join(" ");
  }

  const assignments: { wordIdx: number; chord: string; chordStart: number }[] = [];

  for (const chord of chords) {
    let bestWordIdx = -1;
    let minDist = Infinity;
    let isAfter = false;

    for (let w = 0; w < words.length; w++) {
      const word = words[w];
      // Permitir margen de -2 chars: la palabra puede empezar hasta 2 chars antes del acorde
      if (word.start < chord.start - 2) continue;

      const dist = Math.abs(word.start - chord.start);
      const wordIsAfter = word.start >= chord.start;

      // Elegir si es estrictamente más cercano, o a la misma distancia pero DESPUÉS
      if (dist < minDist || (dist === minDist && wordIsAfter && !isAfter)) {
        minDist = dist;
        bestWordIdx = w;
        isAfter = wordIsAfter;
      }
    }

    // Si no hay palabra dentro del margen, asignar a la última palabra
    if (bestWordIdx === -1) {
      bestWordIdx = words.length - 1;
    }

    assignments.push({ wordIdx: bestWordIdx, chord: chord.chord, chordStart: chord.start });
  }

  // Insertar de derecha a izquierda para no desplazar posiciones.
  // Para mismas palabras, ordenar por posición de acorde descendente
  // para que el acorde más a la derecha se inserte primero.
  assignments.sort((a, b) => {
    if (b.wordIdx !== a.wordIdx) return b.wordIdx - a.wordIdx;
    return b.chordStart - a.chordStart;
  });
  let result = lyricLine;
  for (const { wordIdx, chord } of assignments) {
    const word = words[wordIdx];
    const insertPos = word.start;
    result = result.slice(0, insertPos) + `[${chord}]` + result.slice(insertPos);
  }

  return result;
}

function chordLineToInline(line: string): string {
  const chords: string[] = [];
  let match: RegExpExecArray | null;
  const regex = new RegExp(CHORD_TOKEN.source, "g");
  while ((match = regex.exec(line)) !== null) {
    chords.push(match[0]);
  }
  return chords.map((c) => `[${c}]`).join("");
}

// ============================================
// PASO 7: Pipeline principal
// ============================================
function convertToChordPro(text: string): {
  chordPro: string;
  metadata: ReturnType<typeof extractMetadata>;
} {
  let lines = text.split("\n");

  // 1. Extraer metadatos ANTES de limpiar (para no perder Tono/Capo)
  const metadata = extractMetadata(lines);

  // 2. Limpiar basura
  lines = cleanLines(lines);

  // 3. Eliminar tablatura
  lines = removeTablature(lines);

  // 4. Volver a limpiar (preservar líneas vacías para detectar acordes sueltos)
  lines = lines.filter((l) => {
    const trimmed = l.trim();
    if (trimmed === "") return false;
    // Evitar duplicar metadatos ya extraídos
    if (trimmed === metadata.title || trimmed === metadata.artist) return false;
    return true;
  });

  // 5. Procesar linea por linea
  const result: string[] = [];

  // Header ChordPro
  if (metadata.title) result.push(`{title: ${metadata.title}}`);
  if (metadata.artist) result.push(`{artist: ${metadata.artist}}`);
  if (metadata.key) result.push(`{key: ${metadata.key}}`);
  if (metadata.capo > 0) result.push(`{capo: ${metadata.capo}}`);

  let i = 0;
  while (i < lines.length) {
    const line = lines[i];

    // Linea que empieza con seccion y tiene acordes: [Intro] C G Am
    const sectionChordMatch = line.match(/^(\[[^\]]+\])\s+(.+)$/);
    if (sectionChordMatch && isChordLine(sectionChordMatch[2])) {
      result.push(convertSectionLine(sectionChordMatch[1]));
      result.push(chordLineToInline(sectionChordMatch[2]));
      i++;
      continue;
    }

    // Seccion entre corchetes
    if (isPureSection(line)) {
      result.push(convertSectionLine(line));
      i++;
      continue;
    }

    // Linea de acordes
    if (isChordLine(line)) {
      const nextLine = lines[i + 1] || "";

      // Caso A: siguiente linea tambien es acordes → acordes sueltos
      if (isChordLine(nextLine)) {
        result.push(chordLineToInline(line));
        i++;
        continue;
      }

      // Caso B: siguiente linea es letra → merge
      // PERO: si la siguiente linea es una seccion pura, NO mergear
      if (nextLine && !isChordLine(nextLine) && nextLine.trim() !== "" && !isPureSection(nextLine)) {
        const merged = mergeChordLineWithLyric(line, nextLine);
        result.push(merged);
        i += 2;
        continue;
      }

      // Caso C: siguiente linea vacia, seccion, o no hay → acordes sueltos
      result.push(chordLineToInline(line));
      i++;
      continue;
    }

    // Caso D: linea entre parentesis con acordes
    if (/^\s*\(\s*([A-G][#b]?(?:m|M|maj|7|9|6|sus|dim|aug)?\s+)+\)\s*$/.test(line)) {
      const tokens = line.match(CHORD_TOKEN) || [];
      result.push(tokens.map((t) => `[${t}]`).join(" "));
      i++;
      continue;
    }

    // Letra normal
    result.push(line);
    i++;
  }

  return { chordPro: result.join("\n"), metadata };
}

export function smartPaste(rawText: string): SmartPasteResult {
  const lines = rawText.split("\n");

  // ─── Detección de formato ───
  let format: DetectedFormat = "unknown";

  if (/^CIFRACLUB/i.test(rawText)) {
    format = "cifraclub";
  } else if (/Tono:\s*[A-G]|Capo\s*\d|Acordes:/i.test(rawText)) {
    // Indicadores fuertes de Cifraclub / sitios en español
    format = "cifraclub";
  } else if (
    /^\[?\s*(?:Verse|Chorus|Intro|Bridge)/.test(rawText) ||
    /^Ultimate Guitar/.test(rawText) ||
    rawText.includes("Tabbed by")
  ) {
    format = "ug";
  } else if (/^\{\w+:/.test(rawText)) {
    // Solo si empieza con directiva ChordPro — más estricto
    format = "chordpro";
  }

  // ─── Heurística: líneas de acordes alternadas con letra → cifraclub ───
  // Se ejecuta SIEMPRE, permitiendo sobreescribir la detección inicial
  let hasChords = false;
  let hasLyrics = false;
  let alternatedCount = 0;
  for (let i = 0; i < lines.length; i++) {
    if (isChordLine(lines[i])) hasChords = true;
    else if (lines[i].trim().length > 0 && !lines[i].trim().startsWith("[")) hasLyrics = true;
  }
  for (let i = 0; i < lines.length - 1; i++) {
    if (isChordLine(lines[i]) && !isChordLine(lines[i + 1]) && lines[i + 1].trim().length > 0) {
      alternatedCount++;
    }
  }
  if ((alternatedCount >= 1 && hasChords && hasLyrics) || alternatedCount >= 2) {
    format = "cifraclub";
  }

  const removedLines: string[] = [];
  const originalLines = rawText.split("\n");
  for (const line of originalLines) {
    if (JUNK_PATTERNS.some((p) => p.test(line))) {
      removedLines.push(line);
    }
  }

  let processed = rawText;
  let metadata = { title: "", artist: "", key: "", capo: 0 };

  if (format === "cifraclub" || format === "ug") {
    const conversion = convertToChordPro(rawText);
    processed = conversion.chordPro;
    metadata = conversion.metadata;
  }

  const finalHasChords = /\[([^\[\]]+)\]/.test(processed);
  const finalHasLyrics = processed
    .split("\n")
    .some((l) => l.trim() && !l.trim().startsWith("{") && !/^\s*\[/.test(l));
  const isValid = finalHasChords || finalHasLyrics;

  return {
    format,
    original: rawText,
    processed,
    removedLines,
    hasChords: finalHasChords,
    isValid: isValid && processed.trim().length > 0,
    metadata: metadata.title || metadata.artist ? metadata : undefined,
  };
}
